






























































import useReplies from "@/composables/v2.1/useReplies";
import { useChannelsStore } from "@/store/useChannelsStore";
import { useChatsStore } from "@/store/useChatsStore";
import { resolvePlatformClassByChannel, supportsMessageStatusUpdates } from "@/utils/channels";
import {
    isChatAssignedToMeV21,
    resolveMessageStatusClass,
    resolveMessageStatusIcon,
    resolveMessageStatusText,
    shouldAssignV21,
} from "@/utils/chats";
import { formatDate } from "@/utils/luxon";
import EntryTypeAttachment from "@/views/modules/messaging-inbox/chat-log-entries/attachment.vue";
import AttachmentButtons from "@/views/modules/messaging-inbox/chat-log-entries/components/attachmentButtons.vue";
import Buttons from "@/views/modules/messaging-inbox/chat-log-entries/components/buttons.vue";
import List from "@/views/modules/messaging-inbox/chat-log-entries/components/list.vue";
import Reply from "@/views/modules/messaging-inbox/chat-log-entries/components/reply.vue";
import ReplyButton from "@/views/modules/messaging-inbox/chat-log-entries/components/replyButton.vue";
import TimeStatus from "@/views/modules/messaging-inbox/chat-log-entries/components/timeStatus.vue";
import EntryTypeDate from "@/views/modules/messaging-inbox/chat-log-entries/date.vue";
import EntryTypeEvent from "@/views/modules/messaging-inbox/chat-log-entries/event.vue";
import EntryTypeInformation from "@/views/modules/messaging-inbox/chat-log-entries/information.vue";
import EntryTypeLocation from "@/views/modules/messaging-inbox/chat-log-entries/location.vue";
import EntryTypeText from "@/views/modules/messaging-inbox/chat-log-entries/text.vue";
import { ChatEntry } from "@/views/modules/messaging-inbox/useChat";
import { EntryDirection } from "@saysimple-platform/dtos";
import { computed, defineComponent, PropType, ref } from "@vue/composition-api";
import { useSwipe } from "@vueuse/core";
import { startCase } from "lodash";
import { DateTime } from "luxon";

export default defineComponent({
    methods: { resolveMessageStatusClass, resolveMessageStatusIcon, supportsMessageStatusUpdates },
    components: {
        Buttons,
        EntryTypeAttachment,
        EntryTypeDate,
        EntryTypeEvent,
        EntryTypeInformation,
        EntryTypeLocation,
        EntryTypeText,
        List,
        ReplyButton,
        AttachmentButtons,
        TimeStatus,
        Reply,
    },
    props: {
        entry: { type: Object as PropType<ChatEntry>, required: true },
        groupDirection: {
            type: String as PropType<EntryDirection>,
            required: false,
        },
    },
    setup(props) {
        const chatsStore = useChatsStore();
        const channelsStore = useChannelsStore();

        const replyToEntryId = computed((): number => {
            const replyToEntryId = props.entry.metadata
                ? props.entry.metadata["replyToEntryId"] ?? props.entry.metadata["replyToMessageId"]
                : undefined;

            return replyToEntryId && Number(replyToEntryId);
        });

        const chatEntryClass = computed(() => {
            if (!channelsStore.activeChatChannel || !props.groupDirection) {
                return;
            }

            const platformClass = resolvePlatformClassByChannel(channelsStore.activeChatChannel);
            const style = {
                "pt-50": props.entry.type !== "attachment" || replyToEntryId.value,
            };

            if (!platformClass) {
                return style;
            }

            return {
                ...style,
                [`bg-${platformClass.background} text-${platformClass.text} border-${platformClass.border}`]:
                    props.groupDirection === "out",
            };
        });

        const showReplyButton = computed((): boolean => {
            if (!channelsStore.activeChatChannel || typeof props.entry.id !== "number") {
                return false;
            }

            if (
                !isChatAssignedToMeV21(chatsStore.activeChat.chat.assignedUserId) &&
                shouldAssignV21(channelsStore.activeChatChannel)
            ) {
                return false;
            }

            if (canReply.value && channelsStore.activeChatChannel?.platform === "whatsapp") {
                if (["whatsapp", "messagebird", "bird"].includes(channelsStore.activeChatChannel?.provider)) {
                    return true;
                }
            }

            return false;
        });

        const isAttachment = computed((): boolean => props.entry.type === "attachment");

        //----------------------------------------------------------------------

        const chatEntry = ref(null);
        const { addReply } = useReplies();
        const transformOffset = ref("transform: translateX(0px)");
        const setReplyAfterSwipe = ref(false);
        const { direction, isSwiping, lengthX } = useSwipe(chatEntry, {
            onSwipe(): void {
                if (!canReply.value) {
                    return;
                }

                setReplyAfterSwipe.value = lengthX.value < -48;

                if (lengthX.value < 0) {
                    transformOffset.value = `transform: translateX(${
                        Math.abs(lengthX.value) < 150 ? Math.abs(lengthX.value) : 150
                    }px);`;
                }
            },
            onSwipeEnd(touchEvent, direction): void {
                if (!canReply.value || direction.toLowerCase() !== "right" || !chatsStore.activeChat) {
                    return;
                }

                addReply(chatsStore.activeChat.chat.id, props.entry.id);

                transformOffset.value = "transform: translateX(0px)";
            },
        });

        const canReply = computed((): boolean => {
            // Can't reply on entries older than 30 days
            const today = DateTime.now();
            const entryDate = DateTime.fromISO(String(props.entry.time));

            return today.diff(entryDate, "days").days < 31;
        });

        return {
            canReply,
            channelsStore,
            chatEntry,
            chatEntryClass,
            chatsStore,
            direction,
            formatDate,
            isSwiping,
            lengthX,
            replyToEntryId,
            resolveMessageStatusText,
            resolvePlatformClassByChannel,
            setReplyAfterSwipe,
            showReplyButton,
            isAttachment,
            startCase,
            transformOffset,
        };
    },
});
